import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { toastError } from '../ui/toast';
import CipherHeader from './ui/CipherHeader';
import { colors as theme } from '../theme';

/**
 * Component to view audit logs
 * Only accessible by admins and super_admins
 */
const AuditLogViewer = ({ onBack }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [resourceFilter, setResourceFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('7'); // days
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    loadAuditLogs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter]);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);

      // Calculate date threshold
      const daysAgo = parseInt(dateFilter);
      const dateThreshold = new Date();
      dateThreshold.setDate(dateThreshold.getDate() - daysAgo);

      // Query with joins to get user details
      let query = supabase
        .from('audit_logs')
        .select(`
          *,
          user:user_id (
            id,
            name,
            email,
            role
          )
        `)
        .order('created_at', { ascending: false })
        .limit(500);

      // Add date filter if not "all"
      if (dateFilter !== 'all') {
        query = query.gte('created_at', dateThreshold.toISOString());
      }

      const { data, error } = await query;

      if (error) throw error;
      setLogs(data || []);
    } catch (err) {
      console.error('Error loading audit logs:', err);
      toastError('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  // Filter logs based on search and filters
  const filteredLogs = logs.filter(log => {
    const matchesSearch = !searchTerm ||
      log.action?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.resource_type?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesAction = actionFilter === 'all' || log.action?.startsWith(actionFilter);
    const matchesResource = resourceFilter === 'all' || log.resource_type === resourceFilter;

    return matchesSearch && matchesAction && matchesResource;
  });

  const getActionColor = (action) => {
    if (action?.includes('create')) return theme.success;
    if (action?.includes('update')) return '#5AC8FA';
    if (action?.includes('delete')) return theme.danger;
    if (action?.includes('approve')) return theme.success;
    if (action?.includes('reject')) return theme.danger;
    if (action?.includes('suspend')) return theme.warning;
    return theme.textMuted;
  };

  const getActionIcon = (action) => {
    return '';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div style={{
      padding: '40px',
      backgroundColor: theme.bg,
      minHeight: '100vh'
    }}>
      <CipherHeader title="Audit Logs" subtitle="View system activity" onBack={onBack} />

      {/* Filters */}
      <div style={{
        backgroundColor: theme.surface1,
        border: `1px solid ${theme.surface3}`,
        borderRadius: '14px',
        padding: '24px',
        marginBottom: '24px',
        display: 'flex',
        gap: '16px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Search */}
        <div style={{ flex: '1 1 300px' }}>
          <label style={{ color: theme.textMuted, fontSize: '12px', display: 'block', marginBottom: '8px' }}>
            Search Logs
          </label>
          <input
            type="text"
            placeholder="Search by user, action, or resource..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: theme.surface1,
              border: `2px solid ${theme.surface3}`,
              borderRadius: '8px',
              color: theme.text,
              fontSize: '14px'
            }}
          />
        </div>

        {/* Action Filter */}
        <div style={{ flex: '0 1 200px' }}>
          <label style={{ color: theme.textMuted, fontSize: '12px', display: 'block', marginBottom: '8px' }}>
            Action Type
          </label>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: theme.surface1,
              border: `2px solid ${theme.surface3}`,
              borderRadius: '8px',
              color: theme.text,
              fontSize: '14px'
            }}
          >
            <option value="all">All Actions</option>
            <option value="create">Create</option>
            <option value="update">Update</option>
            <option value="delete">Delete</option>
            <option value="approve">Approve</option>
            <option value="reject">Reject</option>
            <option value="suspend">Suspend</option>
            <option value="assign">Assign</option>
          </select>
        </div>

        {/* Resource Filter */}
        <div style={{ flex: '0 1 200px' }}>
          <label style={{ color: theme.textMuted, fontSize: '12px', display: 'block', marginBottom: '8px' }}>
            Resource Type
          </label>
          <select
            value={resourceFilter}
            onChange={(e) => setResourceFilter(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: theme.surface1,
              border: `2px solid ${theme.surface3}`,
              borderRadius: '8px',
              color: theme.text,
              fontSize: '14px'
            }}
          >
            <option value="all">All Resources</option>
            <option value="user">User</option>
            <option value="program">Program</option>
            <option value="exercise">Exercise</option>
            <option value="workout">Workout</option>
          </select>
        </div>

        {/* Date Filter */}
        <div style={{ flex: '0 1 200px' }}>
          <label style={{ color: theme.textMuted, fontSize: '12px', display: 'block', marginBottom: '8px' }}>
            Time Period
          </label>
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: theme.surface1,
              border: `2px solid ${theme.surface3}`,
              borderRadius: '8px',
              color: theme.text,
              fontSize: '14px'
            }}
          >
            <option value="1">Last 24 hours</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="all">All time</option>
          </select>
        </div>
      </div>

      {/* Results Count */}
      <div style={{ color: theme.textMuted, fontSize: '14px', marginBottom: '16px' }}>
        Showing {filteredLogs.length} of {logs.length} audit logs
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{ color: theme.textMuted, textAlign: 'center', padding: '40px' }}>
          Loading audit logs...
        </div>
      )}

      {/* Audit Logs List */}
      {!loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filteredLogs.map((log) => (
            <div
              key={log.id}
              onClick={() => setSelectedLog(selectedLog?.id === log.id ? null : log)}
              style={{
                backgroundColor: theme.surface1,
                border: `1px solid ${selectedLog?.id === log.id ? theme.gold : theme.surface3}`,
                borderRadius: '14px',
                padding: '20px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              {/* Log Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
                {/* Action Icon */}
                <div style={{
                  fontSize: '24px',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: theme.surface1,
                  borderRadius: '8px'
                }}>
                  {getActionIcon(log.action)}
                </div>

                {/* Log Info */}
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                    <span style={{
                      backgroundColor: getActionColor(log.action),
                      color: theme.text,
                      padding: '4px 12px',
                      borderRadius: '14px',
                      fontSize: '12px',
                      fontWeight: 700
                    }}>
                      {log.action}
                    </span>
                    <span style={{
                      backgroundColor: theme.surface3,
                      color: theme.gold,
                      padding: '4px 12px',
                      borderRadius: '14px',
                      fontSize: '12px',
                      fontWeight: 700
                    }}>
                      {log.resource_type}
                    </span>
                  </div>
                  <div style={{ color: theme.text, fontSize: '14px', fontWeight: 700 }}>
                    {log.user?.name || 'Unknown User'} ({log.user?.email || 'N/A'})
                  </div>
                  <div style={{ color: theme.textMuted, fontSize: '12px' }}>
                    {log.user?.role} • {formatDate(log.created_at)}
                  </div>
                </div>

                {/* Resource ID */}
                {log.resource_id && (
                  <div style={{
                    color: '#5E5E60',
                    fontSize: '12px',
                    backgroundColor: theme.surface1,
                    padding: '6px 12px',
                    borderRadius: '8px'
                  }}>
                    ID: {log.resource_id}
                  </div>
                )}
              </div>

              {/* Expanded Details */}
              {selectedLog?.id === log.id && (
                <div style={{
                  marginTop: '16px',
                  paddingTop: '16px',
                  borderTop: `1px solid ${theme.surface3}`
                }}>
                  {/* Old Values */}
                  {log.old_values && (
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ color: theme.warning, fontSize: '12px', fontWeight: 700, marginBottom: '8px' }}>
                        OLD VALUES:
                      </div>
                      <pre style={{
                        backgroundColor: theme.surface1,
                        color: theme.textMuted,
                        padding: '12px',
                        borderRadius: '8px',
                        fontSize: '12px',
                        overflow: 'auto',
                        maxHeight: '200px'
                      }}>
                        {JSON.stringify(log.old_values, null, 2)}
                      </pre>
                    </div>
                  )}

                  {/* New Values */}
                  {log.new_values && (
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ color: theme.success, fontSize: '12px', fontWeight: 700, marginBottom: '8px' }}>
                        NEW VALUES:
                      </div>
                      <pre style={{
                        backgroundColor: theme.surface1,
                        color: theme.textMuted,
                        padding: '12px',
                        borderRadius: '8px',
                        fontSize: '12px',
                        overflow: 'auto',
                        maxHeight: '200px'
                      }}>
                        {JSON.stringify(log.new_values, null, 2)}
                      </pre>
                    </div>
                  )}

                  {/* Metadata */}
                  <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                    {log.ip_address && (
                      <div>
                        <div style={{ color: '#5E5E60', fontSize: '11px', marginBottom: '4px' }}>IP Address</div>
                        <div style={{ color: theme.textMuted, fontSize: '13px' }}>{log.ip_address}</div>
                      </div>
                    )}
                    {log.user_agent && (
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#5E5E60', fontSize: '11px', marginBottom: '4px' }}>User Agent</div>
                        <div style={{ color: theme.textMuted, fontSize: '13px', wordBreak: 'break-word' }}>
                          {log.user_agent}
                        </div>
                      </div>
                    )}
                    <div>
                      <div style={{ color: '#5E5E60', fontSize: '11px', marginBottom: '4px' }}>Timestamp</div>
                      <div style={{ color: theme.textMuted, fontSize: '13px' }}>
                        {new Date(log.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Empty State */}
          {filteredLogs.length === 0 && (
            <div style={{
              backgroundColor: theme.surface1,
              border: `1px solid ${theme.surface3}`,
              borderRadius: '14px',
              padding: '60px',
              textAlign: 'center',
              color: theme.textMuted
            }}>
              No audit logs found matching your filters.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AuditLogViewer;
